import { OktaAuth } from "@okta/okta-auth-js";
import { writeStorage } from "@rehooks/local-storage";
import { useQueryClient } from "@tanstack/react-query";
import {
  authApiErrorToAuthFailure,
  internalAuthFailure,
} from "client/src/auth/authApiErrorToAuthFailure";
import { useMarkNewSeenDocumentNoticesAsOld } from "client/src/hooks/document";
import { useSlobQuery, useSlobMutation, compareQueryKey } from "client/src/hooks/query";

import { invalidateConfigQuery, useHubConfiguration } from "client/src/hooks/useConfig";
import { usePostLoginActions } from "client/src/hooks/user";
import * as analytics from "client/src/utils/analytics";
import { useLoginFlowAnalytics } from "client/src/utils/mfaAnalytics";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { RouteData } from "shared/config/routeData";

import type { AuthnTransaction, AuthnTransactionFunction } from "@okta/okta-auth-js";

import type { UseMutateAsyncFunction } from "@tanstack/react-query";
import type { AuthFailure } from "client/src/auth/authApiErrorToAuthFailure";
import type {
  PathBodyToVariables,
  ResponseToMutationReturnType,
  ResponseError,
} from "client/src/hooks/query";
import type { LoginFlowStep } from "client/src/utils/mfaAnalytics";
import type { UserData } from "shared/rbac/rbac";
import type { FactorType } from "shared/types/Okta";

export type AuthState = {
  authUser: UserData | null;
};

export const useGetAuthUserStatus = () =>
  useSlobQuery<
    | { authUser: UserData; isAuthenticated: true }
    | { authUser: null; isAuthenticated: false }
    | null
  >({
    method: "get",
    path: "/api/users/authenticated",
    map: (data) => {
      if (!data?.authUser) {
        return {
          isAuthenticated: false,
          authUser: null,
        };
      }

      const authUser: UserData = {
        ...data.authUser,
        clients:
          data.authUser.clients?.map((client) => ({
            ...client,
            completedSetupAt: client.completedSetupAt ? new Date(client.completedSetupAt) : null,
          })) ?? null,
        termsOfUseDate: data.authUser.termsOfUseDate
          ? new Date(data.authUser.termsOfUseDate)
          : null,
      };

      return {
        isAuthenticated: true,
        authUser,
      };
    },
    options: {
      staleTime: 0,
      gcTime: 0,
      refetchOnWindowFocus: "always",
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  });

type EnrollOptions = { profile?: { phoneNumber: string; updatePhone: boolean } };

export interface Factor {
  enroll: ((option?: EnrollOptions) => Promise<AuthnTransaction>) | undefined;
  verify: AuthnTransactionFunction | undefined;
  enrollment: string;
  factorType: FactorType;
  provider: string;
  profile?: {
    phoneNumber?: string;
    email?: string;
  };
  status: "NOT_SETUP" | "PENDING_ACTIVATION" | "ACTIVE" | "INACTIVE";
  vendorName: string;
  activation?: {
    qrcode: {
      href: string;
      type: string;
    };
    factorResult: string; // not used but "WAITING" is an known result. Used for pooling
    sharedSecret: string;
  };
}

export type OktaTransactionResponse = {
  status?: LoginWindowOktaStatuses;
  mfaFactorsToEnroll?: Factor[];
  mfaOptions?: {
    factor?: Factor;
    rememberDeviceLifetimeInMinutes?: number;
  };
  resend?: AuthnTransactionFunction;
  transaction?: AuthnTransaction;
  error?: AuthFailure;
};

export type ChallengeResponse = {
  status?: LoginWindowOktaStatuses;
  mfaOptions?: {
    factor?: Factor;
    rememberDeviceLifetimeInMinutes?: number;
  };
  resend?: AuthnTransactionFunction;
  transaction?: AuthnTransaction;
  error?: AuthFailure;
};

type EnrollReponse = {
  status?: LoginWindowOktaStatuses;
  error?: AuthFailure;
  transaction?: AuthnTransaction;
  mfaOptions?: {
    factor?: Factor;
    rememberDeviceLifetimeInMinutes?: number;
  };
};

type WithErrorResponse = {
  status?: LoginWindowOktaStatuses;
  error?: AuthFailure;
};

export const LOCAL_STORAGE_REMEMBER_USER_KEY = "signin-remember-me-user";

type LoginWindowOktaStatuses =
  | "SUCCESS"
  | "MFA_REQUIRED"
  | "MFA_CHALLENGE"
  | "MFA_ENROLL"
  | "MFA_ENROLL_ACTIVATE";

export const useGetOktaAuth = () => {
  const config = useHubConfiguration();

  const { mutateAsync: updatePostLoginAction } = usePostLoginActions();
  const { markNewDocumentNoticesAsOld } = useMarkNewSeenDocumentNoticesAsOld();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AuthFailure | undefined>(undefined);
  const [shouldRememberUser, setShouldRememberUser] = useState(false);
  const [signinUsername, setSigninUsername] = useState("");

  const {
    start: startLoginAnalytics,
    update: updateLoginAnalytics,
    startEnrollment: startEnrollmentAnalytics,
    submitStep: submitLoginStep,
    failStep: failLoginStep,
    failLogin: failLoginAnalytics,
    completeEnrollment: completeEnrollmentAnalytics,
    completeLogin: completeLoginAnalytics,
    cancel: cancelLoginFlow,
  } = useLoginFlowAnalytics();

  // Every step goes through here: loading starts and the previous error clears,
  // then the step's `{ error }` result (or a thrown error) becomes the hook's error.
  const runStep = useCallback(
    async <T extends { error?: AuthFailure }>(
      step: () => Promise<T>,
      flowStep?: LoginFlowStep,
      factorType?: Factor["factorType"],
    ): Promise<T | { error: AuthFailure }> => {
      setIsLoading(true);
      setError(undefined);
      if (flowStep) submitLoginStep(flowStep, factorType);
      try {
        const result = await step();
        if (result.error) {
          if (flowStep) failLoginStep(flowStep, result.error);
          else failLoginAnalytics(result.error);
        }
        setError(result.error);
        return result;
      } catch (thrown) {
        const failure = authApiErrorToAuthFailure(thrown);
        if (flowStep) failLoginStep(flowStep, failure, thrown);
        else failLoginAnalytics(failure, thrown);
        setError(failure);
        return { error: failure };
      } finally {
        setIsLoading(false);
      }
    },
    [submitLoginStep, failLoginStep, failLoginAnalytics],
  );

  const oktaAuth = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/consistent-type-assertions -- @todo
      ((window as any).oktaAuth = new OktaAuth({
        clientId: config.REACT_APP_OKTA_CLIENT_ID,
        issuer: `${config.REACT_APP_OKTA_BASE_URL}/oauth2/default`,
        scopes: config.REACT_APP_OKTA_SCOPES,
        pkce: true,
        redirectUri: `${window.location.origin}/login/callback`,
      })),
    [config],
  );

  const onSignIn = useCallback(
    async (sessionToken: string) => {
      // This creates an iframe to Okta authorizeUrl, returning tokens in it's callback.
      // This reduces the need of one hard redirect in current page
      // preventing redirect querystring to be lost in the process
      // The no iframe option would be oktaAuth.token.getWithRedirect()
      const { tokens } = await oktaAuth.token.getWithoutPrompt({ sessionToken });

      try {
        await updatePostLoginAction({
          data: {
            accessToken: tokens.accessToken?.accessToken ?? "",
            refreshToken: tokens.refreshToken?.refreshToken ?? "",
          },
        });
        completeLoginAnalytics();
        await markNewDocumentNoticesAsOld();

        writeStorage(LOCAL_STORAGE_REMEMBER_USER_KEY, shouldRememberUser ? signinUsername : "");
      } catch (error) {
        return { error: authApiErrorToAuthFailure(error) };
      }
    },
    [
      markNewDocumentNoticesAsOld,
      oktaAuth.token,
      updatePostLoginAction,
      completeLoginAnalytics,
      shouldRememberUser,
      signinUsername,
    ],
  );

  const getValidFactors = (factors: Factor[]) => factors.filter((f) => f.status !== "NOT_SETUP");
  const getSetupFactors = (factors: Factor[]) =>
    factors.filter((f) => f.status === "NOT_SETUP" || f.status === "PENDING_ACTIVATION");

  const handleOktaTransaction = useCallback(
    async (transaction: AuthnTransaction): Promise<OktaTransactionResponse> => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const factors = (transaction.factors as Factor[] | undefined) || [];

      switch (transaction.status) {
        case "SUCCESS": {
          updateLoginAnalytics("post_login", { oktaUserId: transaction.user?.id });
          completeEnrollmentAnalytics();
          if (!transaction.sessionToken) {
            return {
              error: internalAuthFailure(
                "missing_session_token",
                "Missing sessionToken from response",
              ),
            };
          }
          const res = await onSignIn(transaction.sessionToken);
          return { status: transaction.status, error: res?.error };
        }
        // For Google Authenticator, this is the only step. The user will get the code himself in the app
        case "MFA_REQUIRED": {
          const validFactors = getValidFactors(factors);
          if (!validFactors.length) {
            return {
              error: internalAuthFailure("no_factor", "There's no 2FA factor option configured"),
            };
          }
          if (validFactors.length > 1) {
            return {
              error: internalAuthFailure(
                "multiple_factors",
                "There's more than one 2FA factor option configured",
              ),
            };
          }
          const factor = validFactors[0];
          if (!factor) {
            return {
              error: internalAuthFailure("no_factor", "There's no 2FA factor option configured"),
            };
          }
          updateLoginAnalytics("mfa_challenge", {
            oktaUserId: transaction.user?.id,
            factorType: factor.factorType,
          });
          const mfaOptions = {
            factor,
            rememberDeviceLifetimeInMinutes: transaction.policy?.allowRememberDevice
              ? transaction.policy.rememberDeviceLifetimeInMinutes
              : undefined,
          };
          return { status: transaction.status, mfaOptions };
        }
        // Extra step for a passcode factor (SMS, call, or email) after the code is requested
        case "MFA_CHALLENGE": {
          if (!transaction.factor) {
            return {
              error: internalAuthFailure("no_factor", "There's no 2FA factor option configured"),
            };
          }
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const factor = transaction.factor as Factor;
          updateLoginAnalytics("mfa_challenge", {
            oktaUserId: transaction.user?.id,
            factorType: factor.factorType,
          });
          const mfaOptions = {
            factor,
            rememberDeviceLifetimeInMinutes: transaction.policy?.allowRememberDevice
              ? transaction.policy.rememberDeviceLifetimeInMinutes
              : undefined,
          };
          return {
            status: transaction.status,
            mfaOptions,
            transaction,
          };
        }
        // User doesn't have MFA configured yet
        case "MFA_ENROLL": {
          const setupFactors = getSetupFactors(factors);
          if (!setupFactors.length) {
            return {
              error: internalAuthFailure("no_factor", "There's no 2FA factor option configured"),
            };
          }
          startEnrollmentAnalytics("MFA_ENROLL", {
            oktaUserId: transaction.user?.id,
            offeredFactors: setupFactors,
          });
          return {
            status: transaction.status,
            mfaFactorsToEnroll: setupFactors,
          };
        }
        // After successfully enrolling, we receive QR code for first setup/activation (with code challenge)
        case "MFA_ENROLL_ACTIVATE": {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const factor = transaction.factor as Factor | undefined;
          startEnrollmentAnalytics("MFA_ENROLL_ACTIVATE", {
            oktaUserId: transaction.user?.id,
            factorType: factor?.factorType,
          });
          return {
            status: transaction.status,
            mfaOptions: { factor },
            transaction,
          };
        }
        case "LOCKED_OUT":
          return {
            error: internalAuthFailure(
              "locked_out",
              "Your account is locked for security reasons. Please, try again in about 60 minutes",
            ),
          };
        case "PASSWORD_EXPIRED":
          return {
            error: internalAuthFailure("password_expired", "Your password has expired."),
          };
        default: {
          console.error(`Unknow unhandled process status ${transaction.status}`);
          // https://github.com/okta/okta-auth-js/blob/HEAD/docs/authn.md#transactionstatus
          return {
            error: internalAuthFailure(
              "unhandled_status",
              `Unknow unhandled process status ${transaction.status}`,
            ),
          };
        }
      }
    },
    [onSignIn, startEnrollmentAnalytics, completeEnrollmentAnalytics, updateLoginAnalytics],
  );

  /*
    Possible options
    options: {warnBeforePasswordExpired: true, multiOptionalFactorEnroll: false}
  */
  const signIn = useCallback(
    async ({
      username,
      password,
      rememberMe,
    }: {
      username: string;
      password: string;
      rememberMe: boolean;
    }): Promise<OktaTransactionResponse> => {
      setShouldRememberUser(rememberMe);
      setSigninUsername(username);
      startLoginAnalytics();
      return runStep(async () => {
        const transaction = await oktaAuth.signInWithCredentials({ username, password });
        return await handleOktaTransaction(transaction);
      });
    },
    [oktaAuth, handleOktaTransaction, runStep, startLoginAnalytics],
  );

  const verifyAuthAppMFA = async (
    passcode: string,
    factor: Factor,
    rememberDevice: boolean,
  ): Promise<WithErrorResponse> => {
    updateLoginAnalytics("mfa_challenge", { factorType: factor.factorType, rememberDevice });
    return runStep(async () => {
      if (!factor.verify) {
        return {
          error: internalAuthFailure(
            "inconsistent_transaction",
            "Inconsistent Factor config (verify)",
          ),
        };
      }
      const factorTransaction = await factor.verify({ passCode: passcode, rememberDevice });
      return await handleOktaTransaction(factorTransaction);
    }, "verify");
  };

  const verifyPasscode = async (
    passcode: string,
    transaction: AuthnTransaction,
    rememberDevice: boolean,
  ): Promise<WithErrorResponse> => {
    updateLoginAnalytics("mfa_challenge", { rememberDevice });
    return runStep(async () => {
      if (!transaction.verify) {
        return {
          error: internalAuthFailure(
            "inconsistent_transaction",
            "Inconsistent Factor config (verify)",
          ),
        };
      }
      const factorTransaction = await transaction.verify({ passCode: passcode, rememberDevice });
      return await handleOktaTransaction(factorTransaction);
    }, "verify");
  };

  const enrollFactor = async (factor: Factor): Promise<EnrollReponse> =>
    runStep(
      async () => {
        if (!factor.enroll) {
          return {
            error: internalAuthFailure(
              "inconsistent_transaction",
              "Inconsistent transaction config (factor enroll)",
            ),
          };
        }
        const factorTransaction = await factor.enroll();
        return await handleOktaTransaction(factorTransaction);
      },
      "enroll",
      factor.factorType,
    );

  const enrollMFAPhoneBased = async (factor: Factor, phoneNumber: string): Promise<EnrollReponse> =>
    runStep(
      async () => {
        if (!factor.enroll) {
          return {
            error: internalAuthFailure(
              "inconsistent_transaction",
              "Inconsistent transaction config (factor enroll)",
            ),
          };
        }
        const factorTransaction = await factor.enroll({
          profile: {
            phoneNumber,
            updatePhone: true,
          },
        });
        return await handleOktaTransaction(factorTransaction);
      },
      "enroll",
      factor.factorType,
    );

  const requestPasscode = async (factor: Factor): Promise<ChallengeResponse> => {
    updateLoginAnalytics("mfa_challenge", { factorType: factor.factorType });
    return runStep(async () => {
      if (!factor.verify) {
        return {
          error: internalAuthFailure(
            "inconsistent_transaction",
            "Inconsistent Factor config (verify)",
          ),
        };
      }
      const factorTransaction = await factor.verify(); // empty verify sends the passcode
      return await handleOktaTransaction(factorTransaction);
    }, "send");
  };

  const resendPasscode = async (
    transaction: AuthnTransaction,
    factorType: Factor["factorType"],
  ): Promise<ChallengeResponse> =>
    runStep(
      async () => {
        if (!transaction.resend) {
          return {
            error: internalAuthFailure(
              "inconsistent_transaction",
              "Inconsistent Transaction config (resend)",
            ),
          };
        }
        const factorTransaction = await transaction.resend(factorType);
        return await handleOktaTransaction(factorTransaction);
      },
      "resend",
      factorType,
    );

  const setupMFA = async (
    passcode: string,
    transaction: AuthnTransaction,
  ): Promise<WithErrorResponse> =>
    runStep(async () => {
      if (!transaction.activate) {
        return {
          error: internalAuthFailure(
            "inconsistent_transaction",
            "Inconsistent transaction config (activate)",
          ),
        };
      }
      const transactionActivationResponse = await transaction.activate({ passCode: passcode });
      return await handleOktaTransaction(transactionActivationResponse);
    }, "activate");

  const returnToFactorSelection = async (
    transaction: AuthnTransaction,
  ): Promise<OktaTransactionResponse> =>
    runStep(async () => {
      if (!transaction.prev) {
        return {
          error: internalAuthFailure(
            "inconsistent_transaction",
            "Inconsistent transaction config (prev)",
          ),
        };
      }
      const previousTransaction = await transaction.prev();
      return await handleOktaTransaction(previousTransaction);
    }, "change_factor");

  return {
    oktaAuth,
    cancelLoginFlow,
    // Records the factor before any request, so a flow abandoned on the next screen keeps it.
    trackFactorSelected: (factor: Factor) => submitLoginStep("select", factor.factorType),
    // For a factor change that needs no Okta request; `returnToFactorSelection` records its own.
    trackFactorChange: () => submitLoginStep("change_factor"),
    // For failures that the login screens detect after a successful Okta response.
    trackLoginFailure: failLoginAnalytics,
    oktaStep: {
      signIn,
      verifyAuthAppMFA,
      verifyPasscode,
      enrollFactor,
      enrollMFAPhoneBased,
      requestPasscode,
      resendPasscode,
      setupMFA,
      returnToFactorSelection,
    },
    isLoading,
    error,
  };
};

export type OktaStep = ReturnType<typeof useGetOktaAuth>["oktaStep"];

export type LogoutPostType = UseMutateAsyncFunction<
  ResponseToMutationReturnType<{
    success: boolean;
  }>,
  ResponseError,
  PathBodyToVariables<"/api/users/authenticated/logout", unknown, never>,
  unknown
>;

export const useLogout = () => {
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  return useSlobMutation<unknown, { success: boolean }, `/api/users/authenticated/logout`>({
    method: "post",
    path: `/api/users/authenticated/logout`,
    options: {
      async onSuccess() {
        // eslint-disable-next-line react-compiler/react-compiler
        delete window.client;
        analytics.reset();
        await Promise.all([
          invalidateConfigQuery(queryClient),
          queryClient.invalidateQueries({
            predicate: compareQueryKey(["get", `/api/users/authenticated`]),
          }),
        ]);
        await navigate(RouteData.login.getPathTemplate());
      },
    },
  });
};
